/**
 * deck.gl overlay on MapLibre via MapboxOverlay.
 *
 * MapboxOverlay is the official deck.gl + MapLibre integration. It:
 * - Creates and manages the WebGL canvas automatically
 * - Syncs deck.gl view state with MapLibre camera (no manual sync needed)
 * - Handles pointer-events coordination (no MutationObserver hack needed)
 * - Supports built-in onClick/onHover picking callbacks
 */

import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Layer } from '@deck.gl/core';
import type maplibregl from 'maplibre-gl';

let overlayInstance: MapboxOverlay | null = null;

export interface GlobeOptions {
  map: maplibregl.Map;
}

/** Initialize the deck.gl overlay as a MapLibre control. */
export function initGlobe(options: GlobeOptions): MapboxOverlay {
  overlayInstance = new MapboxOverlay({
    interleaved: true,
    layers: [],
    pickingRadius: 10,
    useDevicePixels: window.devicePixelRatio > 2 ? 2 : true,
  });

  options.map.addControl(overlayInstance as unknown as maplibregl.IControl);

  return overlayInstance;
}

/** Update the layers rendered by deck.gl. */
export function setDeckLayers(layers: Layer[]): void {
  if (!overlayInstance) return;
  overlayInstance.setProps({ layers });
}

/** Get the current MapboxOverlay instance. */
export function getOverlay(): MapboxOverlay | null {
  return overlayInstance;
}

/** Destroy the deck.gl overlay. */
export function destroyGlobe(): void {
  if (overlayInstance) {
    overlayInstance.finalize();
    overlayInstance = null;
  }
}
